import React, { useEffect, useState, useRef } from 'react';
import { MusicIcon, XIcon, CheckIcon, SearchIcon } from 'lucide-react';
interface InstrumentFilterDropdownProps {
  selectedInstruments: string[];
  onChange: (instruments: string[]) => void;
  darkMode?: boolean;
}
export const InstrumentFilterDropdown: React.FC<InstrumentFilterDropdownProps> = ({
  selectedInstruments,
  onChange,
  darkMode = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  // Instrument categories and options
  const instrumentCategories = [{
    category: 'Traditional Instruments',
    instruments: ['Piano', 'Guitar', 'Violin', 'Viola', 'Cello', 'Drums', 'Flute', 'Clarinet', 'Saxophone', 'Trumpet', 'Voice', 'Bass', 'Ukulele']
  }, {
    category: 'Music Technology & Theory',
    instruments: ['Music Production', 'Mixing', 'Mastering', 'Sound Design', 'Music Theory', 'Ear Training', 'Composition', 'Arranging', 'Recording Techniques', 'Film Scoring']
  }, {
    category: 'Software',
    instruments: ['Ableton Live', 'Logic Pro', 'FL Studio', 'Pro Tools', 'Cubase']
  }];
  const toggleInstrument = (instrument: string) => {
    if (selectedInstruments.includes(instrument)) {
      onChange(selectedInstruments.filter(i => i !== instrument));
    } else {
      onChange([...selectedInstruments, instrument]);
    }
  };
  // Filter instruments based on search query
  const filteredCategories = instrumentCategories.map(category => ({
    ...category,
    instruments: category.instruments.filter(instrument => instrument.toLowerCase().includes(searchQuery.toLowerCase()))
  })).filter(category => category.instruments.length > 0);
  // Get all instruments for checking if we have selected any
  const allInstruments = instrumentCategories.flatMap(category => category.instruments);
  return <div className="relative" ref={dropdownRef} data-id="element-1470">
      {/* Dropdown Button */}
      <button onClick={() => setIsOpen(!isOpen)} className={`flex items-center justify-between w-full px-4 py-2.5 text-sm rounded-lg ${darkMode ? 'bg-navy-900/50 text-white border-navy-700 hover:bg-navy-800' : 'bg-gray-50 text-gray-800 border-gray-300 hover:bg-gray-100'} border transition-colors duration-200`} data-id="element-1471">
        <span className="truncate pr-6 flex items-center" data-id="element-1472">
          <MusicIcon className="h-4 w-4 mr-2" data-id="element-1473" />
          {selectedInstruments.length === 0 ? 'All Instruments' : selectedInstruments.length === 1 ? selectedInstruments[0] : `${selectedInstruments.length} selected`}
        </span>
        <svg className={`h-5 w-5 transform transition-transform ${isOpen ? 'rotate-180' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1474">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-1475" />
        </svg>
      </button>
      {/* Dropdown Menu */}
      {isOpen && <div className={`absolute z-50 mt-2 w-72 ${darkMode ? 'bg-[#1A2A52] text-white' : 'bg-white text-gray-800'} rounded-lg shadow-lg overflow-hidden border ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-1476">
          <div className={`p-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-1477">
            <div className="relative mb-3" data-id="element-1478">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1479">
                <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-1480" />
              </div>
              <input type="text" className={`pl-10 pr-4 py-2.5 w-full text-sm rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} focus:ring-deep-pink focus:border-deep-pink border`} placeholder="Search instruments..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1481" />
            </div>
            {/* Selected Instruments */}
            {selectedInstruments.length > 0 && <div className="mb-3" data-id="element-1482">
                <div className="flex flex-wrap gap-1.5" data-id="element-1483">
                  {selectedInstruments.map(instrument => <div key={instrument} className={`flex items-center px-2 py-1 rounded-full text-xs ${darkMode ? 'bg-navy-700 text-white' : 'bg-red-50 text-gray-800'}`} data-id="element-1484">
                      {instrument}
                      <button type="button" onClick={e => {
                e.stopPropagation();
                toggleInstrument(instrument);
              }} className="ml-1.5 text-gray-400 hover:text-gray-600" data-id="element-1485">
                        <XIcon className="h-3 w-3" data-id="element-1486" />
                      </button>
                    </div>)}
                </div>
                <button type="button" onClick={() => onChange([])} className={`mt-2 text-xs ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-1487">
                  Clear all
                </button>
              </div>}
            <div className="max-h-60 overflow-y-auto pr-2 -mr-2" data-id="element-1488">
              {filteredCategories.map((category, index) => <div key={index} className="mb-3" data-id="element-1489">
                  <h4 className={`text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1490">
                    {category.category}
                  </h4>
                  <div className="grid grid-cols-1 gap-1" data-id="element-1491">
                    {category.instruments.map(instrument => <div key={instrument} className={`flex items-center px-4 py-2.5 text-sm rounded-lg cursor-pointer ${selectedInstruments.includes(instrument) ? darkMode ? 'bg-[#0A1128] border-l-2 border-deep-pink' : 'bg-red-50 border-l-2 border-deep-pink' : darkMode ? 'hover:bg-[#0A1128]/50' : 'hover:bg-gray-100'}`} onClick={() => toggleInstrument(instrument)} data-id="element-1492">
                        <div className={`h-3.5 w-3.5 rounded flex-shrink-0 mr-2 flex items-center justify-center ${selectedInstruments.includes(instrument) ? 'bg-[#10B981] text-white' : darkMode ? 'border border-gray-600' : 'border border-gray-300'}`} data-id="element-1493">
                          {selectedInstruments.includes(instrument) && <CheckIcon className="h-2.5 w-2.5" data-id="element-1494" />}
                        </div>
                        <span data-id="element-1495">{instrument}</span>
                      </div>)}
                  </div>
                </div>)}
              {filteredCategories.length === 0 && <div className={`text-center py-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1496">
                  No instruments found matching "{searchQuery}"
                </div>}
            </div>
          </div>
        </div>}
    </div>;
};